import { useEffect } from 'react';
import { useRealtime } from '@core/realtime/RealtimeProvider';
import { liveQuotesStore } from './liveQuotesStore';

/**
 * Mounts the SignalR `quotes.update` listener exactly once and writes each
 * incoming quote into the singleton `liveQuotesStore`.
 *
 * Mount in <AppShell />, not in TradesTable — we want quotes accumulating
 * even when the user isn't on the trades page, so navigating to it shows the
 * latest value instantly.
 */
export function useLiveQuotesListener(): void {
  const realtime = useRealtime();
  useEffect(() => {
    const offQuotes = realtime.subscribe('quotes.update', (payload) => {
      if (!payload?.symbol) return;
      liveQuotesStore.set(payload.symbol, {
        last: payload.last ?? null,
        bid:  payload.bid  ?? null,
        ask:  payload.ask  ?? null,
        ts:   payload.ts,
      });
    });
    // When the CQG session goes down, cached quotes are stale (no live feed) — clear them so rows show
    // "—" instead of a frozen last price after a disconnect. Fresh quotes repopulate on reconnect.
    // (A browser-socket drop also clears via cqgQuoteSubscriber; this covers the BE session state.)
    const offState = realtime.subscribe('cqg.state', (payload) => {
      const s = (payload.state ?? '').toLowerCase();
      if (s === 'disconnected' || s === 'error') liveQuotesStore.clear();
    });
    return () => { offQuotes(); offState(); };
  }, [realtime]);
}
